import { LuTrash2 } from '@react-icons/all-files/lu/LuTrash2'
import React, { useMemo, useState } from 'react'
import { useDispatch } from 'hooks/use-redux'

import { deleteEmailGroups } from '@disco/state-management/actions/EmailGroupActions'

import type { MailingList, RecipientListId } from 'types/list'
import type { SelectableItem } from 'types/selection'

import { Button, Dialog, Flex, Text } from 'components/ds'
import ExplorerActionMenuContainer from 'components/explorer/action-menu/explorer-action-menu-container'

import styles from './mailing-lists.module.css'

type Props = {
  selectedLists: MailingList[]
  /** Called with the ids that were actually deleted - a partial failure leaves the rest selected. */
  onDeleted: (_deletedIds: RecipientListId[]) => void
}

/**
 * Floating bulk-actions bar for the mailing lists table, following the
 * multi-select action menu pattern from Browse.
 */
const MailingListsSelectionMenu = ({
  selectedLists,
  onDeleted,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const [confirmOpen, setConfirmOpen] = useState(false)
  // Local to this batch rather than the shared redux `isDeleting` flag, which
  // the first of several concurrent deletes to settle would otherwise reset
  // to false while the rest are still in flight.
  const [isSubmitting, setIsSubmitting] = useState(false)

  // The container only reads `type` to build its "N lists selected" label
  const selectionItems = useMemo(
    () =>
      selectedLists.map(list => ({
        ...list,
        type: 'list',
      })) as unknown as SelectableItem[],
    [selectedLists],
  )

  const handleDelete = async (evt: React.FormEvent) => {
    evt.preventDefault()
    setIsSubmitting(true)
    const deletedIds = await dispatch(deleteEmailGroups(selectedLists))
    setIsSubmitting(false)
    setConfirmOpen(false)
    onDeleted(deletedIds)
  }

  return (
    <>
      <ExplorerActionMenuContainer
        selectedItems={selectionItems}
        label={`${selectedLists.length} lists selected`}
        className={styles.actionMenu}
      >
        <Button
          type="button"
          color="red"
          variant="soft"
          onClick={() => setConfirmOpen(true)}
        >
          <LuTrash2 size={16} />
          Delete
        </Button>
      </ExplorerActionMenuContainer>

      <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <Dialog.Content maxWidth="400px" aria-describedby={undefined}>
          <Dialog.Title>
            {`Delete ${selectedLists.length} ${
              selectedLists.length === 1 ? 'list' : 'lists'
            }`}
          </Dialog.Title>
          <form onSubmit={handleDelete}>
            <Text size="2">
              {`Are you sure you want to permanently delete ${
                selectedLists.length === 1 ? 'this list' : 'these lists'
              }? This can’t be undone.`}
            </Text>
            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button
                  type="button"
                  variant="soft"
                  color="gray"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </Dialog.Close>
              <Button type="submit" color="red" loading={isSubmitting}>
                Delete
              </Button>
            </Flex>
          </form>
        </Dialog.Content>
      </Dialog.Root>
    </>
  )
}

export default MailingListsSelectionMenu
